import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatDateTime } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ChatCircleDots, WhatsappLogo, FilePdf, Image as ImageIcon, MicrophoneStage, VideoCamera, ArrowsClockwise, Robot, HandPalm, PaperPlaneTilt, Paperclip, X, Check, Checks, WarningCircle, HandCoins } from "@phosphor-icons/react";

const STATUS = {
  respondida: { label: "Respondida", cls: "green" },
  aguardando: { label: "Aguardando", cls: "medio" },
  nova: { label: "Nova", cls: "neutral" },
};

const CLASS = {
  vai_pagar: { l: "Vai pagar", c: "#3F6B45" },
  solicitou_prazo: { l: "Solicitou prazo", c: "#C9A227" },
  sem_resposta: { l: "Sem resposta", c: "#D97A34" },
  recusa: { l: "Recusa pagamento", c: "#B4564F" },
  numero_invalido: { l: "Numero invalido", c: "#2A2A2A" },
  enviou_comprovante: { l: "Enviou comprovante", c: "#2D6CB4" },
};

const FILTERS = [["", "Todas"], ["unread", "Nao lidas"], ["overdue", "Atrasadas"], ["pending_receipt", "Pend. comprovante"]];

function ClassDot({ k, label = false }) {
  const c = CLASS[k];
  if (!c) return null;
  return (
    <span className="inline-flex items-center gap-1" data-testid={`class-${k}`}>
      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.c }} />
      {label && <span className="text-[10px] tracking-widest uppercase" style={{ color: c.c }}>{c.l}</span>}
    </span>
  );
}

function StatusTicks({ status }) {
  const s = (status || "").toString().toUpperCase();
  if (["ERROR", "FAILED", "NOT-DELIVERED", "NOTDELIVERED"].includes(s)) {
    return <WarningCircle size={12} weight="fill" className="text-[#B4564F]" data-testid="msg-status-error"><title>Falha no envio</title></WarningCircle>;
  }
  const read = s === "READ" || s === "PLAYED" || s === "VIEWED";
  const delivered = read || s === "RECEIVED" || s === "DELIVERED";
  if (delivered) {
    return <Checks size={13} weight="bold" style={{ color: read ? "#53BDEB" : "#9A938C" }} data-testid={read ? "msg-status-read" : "msg-status-delivered"} />;
  }
  // SENT ou ainda sem status (envio aceito pela Z-API) -> um traco
  return <Check size={13} weight="bold" className="text-[#9A938C]" data-testid="msg-status-sent" />;
}

function MediaBubble({ m }) {
  const mediaSrc = m.media_url || m.media_base64;
  if (m.type === "image") {
    return mediaSrc
      ? <img src={mediaSrc} alt="imagem" className="max-w-[220px] border border-[#EBE5DF] rounded" />
      : <span className="flex items-center gap-1 text-xs"><ImageIcon size={14} /> Imagem{m.caption ? `: ${m.caption}` : ""}</span>;
  }
  if (m.type === "document") {
    const inner = <span className="flex items-center gap-1 text-xs"><FilePdf size={14} /> {m.media_filename || "Documento"}</span>;
    return mediaSrc ? <a href={mediaSrc} target="_blank" rel="noreferrer" download={m.media_filename || "arquivo"} className="underline">{inner}</a> : inner;
  }
  if (m.type === "audio") return mediaSrc
    ? <audio controls src={mediaSrc} className="max-w-[220px]" />
    : <span className="flex items-center gap-1 text-xs"><MicrophoneStage size={14} /> Audio</span>;
  if (m.type === "video") return <span className="flex items-center gap-1 text-xs"><VideoCamera size={14} /> Video{m.text ? `: ${m.text}` : ""}</span>;
  if (m.type === "reaction") return <span className="text-lg">{m.text}</span>;
  return <span className="text-sm whitespace-pre-wrap break-words">{m.text || "[mensagem]"}</span>;
}

export default function CRMPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [resellerFilter, setResellerFilter] = useState("");
  const [resellers, setResellers] = useState([]);
  const [composer, setComposer] = useState("");
  const [attach, setAttach] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const loadConvos = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (isAdmin && resellerFilter) params.reseller_id = resellerFilter;
      setConvos((await api.get("/whatsapp/conversations", { params })).data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (isAdmin && resellerFilter) params.reseller_id = resellerFilter;
        setConvos((await api.get("/whatsapp/conversations", { params })).data);
      } catch { /* ignore */ }
    })();
  }, [statusFilter, resellerFilter, isAdmin]);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => { try { setResellers((await api.get("/whatsapp/resellers")).data); } catch { /* ignore */ } })();
  }, [isAdmin]);

  const openConvo = async (clientId, silent = false) => {
    setActive(clientId);
    if (!silent) setLoadingThread(true);
    try {
      const { data } = await api.get(`/whatsapp/conversations/${clientId}`);
      setThread(data);
      if (!silent) loadConvos();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { /* ignore */ } finally { setLoadingThread(false); }
  };

  const toggleAI = async () => {
    if (!thread) return;
    const on = thread.conversation?.ai_enabled !== false;
    try {
      await api.post(`/whatsapp/conversations/${thread.client.id}/${on ? "assume" : "resume"}`);
      toast.success(on ? "Voce assumiu o atendimento. A IA foi pausada." : "IA reativada para esta conversa.");
      openConvo(thread.client.id, true);
    } catch { toast.error("Erro ao alterar atendimento"); }
  };

  const classifyManual = async (clientId, classification) => {
    if (!classification) return;
    try {
      await api.post(`/whatsapp/conversations/${clientId}/classify`, { classification });
      toast.success("Conversa classificada");
      openConvo(clientId, true);
      loadConvos();
    } catch { toast.error("Erro ao classificar"); }
  };

  const sendPix = async () => {
    if (!thread) return;
    if (!window.confirm("Enviar a chave PIX da revendedora para a cliente? Isso fica registrado para auditoria.")) return;
    try {
      await api.post(`/whatsapp/conversations/${thread.client.id}/send-pix`);
      toast.success("Chave PIX enviada e registrada (PIX direto)");
      openConvo(thread.client.id, true);
      loadConvos();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar PIX"); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Arquivo muito grande (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setAttach({ data: reader.result, filename: f.name, mimetype: f.type });
    reader.readAsDataURL(f);
  };

  const sendReply = async () => {
    if (!thread) return;
    if (!composer.trim() && !attach) return;
    setSending(true);
    try {
      const payload = { text: composer.trim() || undefined };
      if (attach) payload.media = attach;
      await api.post(`/whatsapp/conversations/${thread.client.id}/reply`, payload);
      setComposer(""); setAttach(null);
      if (fileRef.current) fileRef.current.value = "";
      openConvo(thread.client.id, true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="crm-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Atendimento e cobranca</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">CRM Conversas</h1>
        </div>
        <button onClick={loadConvos} className="text-xs tracking-widest uppercase font-medium hover:underline flex items-center gap-1" data-testid="crm-refresh-btn"><ArrowsClockwise size={14} /> Atualizar</button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="crm-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} onClick={() => setStatusFilter(k)} data-testid={`crm-filter-${k || "all"}`}
            className={`text-xs tracking-widest uppercase px-3 py-1.5 border transition-colors ${statusFilter === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#EBE5DF] hover:bg-[#FAF7F3]"}`}>
            {label}
          </button>
        ))}
        {isAdmin && (
          <select value={resellerFilter} onChange={(e) => setResellerFilter(e.target.value)} className="text-xs border border-[#EBE5DF] px-2 py-1.5 ml-auto" data-testid="crm-reseller-filter">
            <option value="">Todas as revendedoras</option>
            {resellers.map((rv) => <option key={rv.id} value={rv.id}>{rv.name}</option>)}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-0 border border-[#EBE5DF] bg-white" style={{ minHeight: "60vh" }}>
        {/* Lista de conversas */}
        <div className="border-r border-[#EBE5DF] overflow-y-auto" style={{ maxHeight: "72vh" }} data-testid="crm-convo-list">
          {convos.length === 0 && (
            <div className="p-8 text-center text-sm text-[#5C5A57]">
              <WhatsappLogo size={36} weight="duotone" className="mx-auto mb-2 text-[#25D366]" />
              Nenhuma conversa neste filtro. As mensagens recebidas das clientes aparecerao aqui.
            </div>
          )}
          {convos.map((c) => (
            <button key={c.client_id} onClick={() => openConvo(c.client_id)}
              className={`w-full text-left px-4 py-3 border-b border-[#F0EBE5] hover:bg-[#FAF7F3] transition-colors ${active === c.client_id ? "bg-[#F5F0EB]" : ""}`}
              data-testid={`crm-convo-${c.client_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{c.client_name || c.phone}</span>
                {c.unread_count > 0 && <span className="bg-[#25D366] text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center" data-testid={`crm-unread-${c.client_id}`}>{c.unread_count}</span>}
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs text-[#5C5A57] truncate">{c.last_direction === "out" ? "Voce: " : ""}{c.last_text}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className={`badge ${STATUS[c.status]?.cls || "neutral"}`} style={{ fontSize: "9px" }}>{STATUS[c.status]?.label || c.status}</span>
                <div className="flex items-center gap-2">
                  {c.classification && <ClassDot k={c.classification} />}
                  <span className="text-[10px] text-[#9A938C]">{c.last_at ? formatDateTime(c.last_at) : ""}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="flex flex-col" style={{ maxHeight: "72vh" }}>
          {!thread && (
            <div className="flex-1 grid place-items-center text-sm text-[#5C5A57] p-8" data-testid="crm-empty-thread">
              <div className="text-center"><ChatCircleDots size={40} weight="duotone" className="mx-auto mb-2" />Selecione uma conversa para ver o historico.</div>
            </div>
          )}
          {thread && (
            <>
              <div className="px-5 py-3 border-b border-[#EBE5DF]">
                <div className="flex items-center justify-between">
                  <div>
                    <Link to={`${isAdmin ? "/admin/clientes" : "/revenda/clientes"}/${thread.client.id}`} className="font-display text-lg font-bold hover:underline inline-flex items-center gap-1" data-testid="crm-client-link">{thread.client.name}</Link>
                    <div className="text-xs text-[#5C5A57]">{thread.client.phone}</div>
                    {thread.reseller && (
                      <Link to={isAdmin ? `/admin/revendedoras/${thread.reseller.id}` : "/revenda/perfil"} className="text-[10px] tracking-widest uppercase text-[#3F6B45] hover:underline" data-testid="crm-reseller-link">
                        Revendedora: {thread.reseller.name}
                      </Link>
                    )}
                  </div>
                  {thread.conversation?.last_at && <div className="text-[10px] text-[#9A938C]">Ultima interacao: {formatDateTime(thread.conversation.last_at)}</div>}
                </div>
                <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    {thread.conversation?.classification && <ClassDot k={thread.conversation.classification} label />}
                    {(thread.conversation?.tags || []).includes("Pagamento Direto Revendedora") && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-medium text-[#3F6B45] border border-[#3F6B45] px-2 py-0.5" data-testid="crm-pix-direct-tag"><HandCoins size={12} weight="duotone" /> Pagamento Direto Revendedora</span>
                    )}
                    <select onChange={(e) => { classifyManual(thread.client.id, e.target.value); }} value="" className="text-[11px] border border-[#EBE5DF] px-2 py-1" data-testid="classify-select">
                      <option value="">Classificar...</option>
                      {Object.entries(CLASS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={sendPix} className="flex items-center gap-1.5 text-xs tracking-widest uppercase font-medium px-3 py-1.5 text-[#3F6B45] border border-[#3F6B45] hover:bg-[#EAF1EA] transition-colors" data-testid="crm-send-pix-btn" title="Enviar a chave PIX da revendedora (somente quando a cliente solicitar)"><HandCoins size={15} weight="duotone" /> Enviar PIX</button>
                    <button onClick={toggleAI} className={`flex items-center gap-2 text-xs tracking-widest uppercase font-medium px-3 py-1.5 ${thread.conversation?.ai_enabled !== false ? "text-[#B4564F]" : "text-[#3F6B45]"}`} data-testid="toggle-ai-btn">
                      {thread.conversation?.ai_enabled !== false ? <><HandPalm size={16} weight="duotone" /> Assumir atendimento</> : <><Robot size={16} weight="duotone" /> Voltar para IA</>}
                    </button>
                  </div>
                </div>
                {thread.conversation?.ai_enabled === false && (
                  <div className="text-[10px] tracking-widest uppercase text-[#B4564F] mt-1" data-testid="ai-paused-badge">IA pausada — atendimento humano</div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-[#FAF7F3]" data-testid="crm-thread">
                {loadingThread && <div className="text-center text-xs tracking-widest uppercase text-[#5C5A57]">Carregando...</div>}
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-lg ${m.direction === "out" ? "bg-[#DCF8C6]" : "bg-white border border-[#EBE5DF]"}`} data-testid={`crm-msg-${m.id}`}>
                      <MediaBubble m={m} />
                      <div className="text-[9px] text-[#9A938C] mt-1 flex items-center gap-1 justify-end">
                        <span>{m.ai ? "IA · " : m.manual ? "Voce · " : ""}{formatDateTime(m.created_at)}</span>
                        {m.direction === "out" && <StatusTicks status={m.delivery_status} />}
                      </div>
                    </div>
                  </div>
                ))}
                {thread.messages.length === 0 && <div className="text-center text-sm text-[#5C5A57]">Sem mensagens nesta conversa.</div>}
                <div ref={bottomRef} />
              </div>

              {/* Composer (atendimento humano - numero oficial) */}
              <div className="border-t border-[#EBE5DF] p-3 bg-white" data-testid="crm-composer">
                {attach && (
                  <div className="flex items-center gap-2 text-xs text-[#5C5A57] mb-2 bg-[#FAF7F3] border border-[#EBE5DF] px-2 py-1" data-testid="crm-attach-preview">
                    <Paperclip size={14} /> <span className="truncate flex-1">{attach.filename}</span>
                    <button onClick={() => { setAttach(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="crm-attach-remove"><X size={14} /></button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onPickFile} className="hidden" data-testid="crm-file-input" />
                  <button onClick={() => fileRef.current?.click()} className="border border-[#EBE5DF] p-2 hover:bg-[#FAF7F3] transition-colors" title="Anexar imagem ou PDF" data-testid="crm-attach-btn"><Paperclip size={18} /></button>
                  <textarea value={composer} onChange={(e) => setComposer(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    rows={1} placeholder="Digite uma mensagem... (Enter envia)" className="flex-1 resize-none p-2 border border-[#EBE5DF] text-sm" data-testid="crm-composer-input" />
                  <button onClick={sendReply} disabled={sending || (!composer.trim() && !attach)} className="brand disabled:opacity-40 inline-flex items-center gap-2" data-testid="crm-send-btn">
                    <PaperPlaneTilt size={16} weight="fill" /> {sending ? "..." : "Enviar"}
                  </button>
                </div>
                <div className="text-[10px] text-[#9A938C] mt-1">Enviado pelo numero oficial. Se a IA estiver ativa, use a opcao Assumir atendimento para pausa-la.</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
